import {assignmentPlanForLegs, needsAssignmentReview} from '../../../common/flight-leg/assignment-plan';
import {RouteVisit, orderedLocations} from '../../../common/flight-leg/route-visits';
import {getFlightLegManagerFromFlightQuery} from '../../../common/flight-leg/util';
import {FlightLeg} from '../../../schema';

export const routeFingerprint = (flight: any) => JSON.stringify([flight?._id, flight?.departureID?._id, flight?.legs]);

/**
 * Builds the exact authored route and its assignment review plan.
 * @param options - Allows a different first stop only after departure-change confirmation.
 * Retained passenger/cargo endpoints follow the server's existing departure remapping.
 */
export async function prepareRouteEdit(
    flight: any,
    initialVisits: RouteVisit[],
    visits: RouteVisit[],
    resolveLocations: (ids: string[]) => Promise<any[]>,
    draftLegs?: FlightLeg[],
    options?: {allowDepartureChange?: boolean}
) {
    if (!visits[0]?.locationID)
        throw new Error('Add a departure location before reviewing the route.');
    if (
        !options?.allowDepartureChange &&
        (!flight.departureID?._id || visits[0].locationID !== flight.departureID._id)
    )
        throw new Error(
            'The route must start at the flight departure. Use the repair action first.'
        );
    const ids = visits.map((visit) => visit.locationID);
    const locations = orderedLocations(ids, await resolveLocations(Array.from(new Set(ids))));
    const manager = getFlightLegManagerFromFlightQuery(
        draftLegs ? {...flight, legs: draftLegs} : flight,
        new Map(locations.map((location) => [location._id, location.name]))
    );
    // SetFlight remaps retained PAX/CGO endpoints when departureID changes.
    // Match that preview without rewriting any of the user's authored stops.
    if (options?.allowDepartureChange && flight.departureID?._id) {
        const entities = [
            ...Array.from(manager.allPax.values()),
            ...Array.from(manager.allCgo.values()),
        ];
        for (const entity of entities) {
            if (entity.departureID === flight.departureID._id)
                entity.departureID = visits[0].locationID;
            if (entity.destinationID === flight.departureID._id)
                entity.destinationID = visits[0].locationID;
        }
    }
    if (!draftLegs) manager.bindInitialVisits(initialVisits);
    manager.setRouteVisits(visits);
    return manager.buildFlightLegs({disableCreateMissingLegs: true});
}

/** Builds one tenant-scoped mutation for route, memberships, and an optional changed departure. */
export function routeMutationVariables(
    flightID: string,
    legs: FlightLeg[],
    tenant: {tpID: string; customerID: string; orgID: string},
    departureID?: string
) {
    return {
        payload: {
            ...(departureID ? {departureID} : {}),
            _id: flightID,
            legs: JSON.stringify(Object.assign({}, legs)),
            paxIDList: Array.from(new Set(legs.flatMap((leg) => leg.paxIDs))),
            cgoIDList: Array.from(new Set(legs.flatMap((leg) => leg.cgoIDs))),
            tpID: tenant.tpID,
            customerID: tenant.customerID,
        },
        orgID: tenant.orgID,
    };
}

/**
 * Saves authored route changes while rejecting stale drafts and duplicate submissions.
 *
 * @remarks
 * Review receives a persistence callback so a dialog can save before closing and
 * retry failures without losing choices. Callers that only return reviewed legs
 * retain the existing save-after-review behavior. Cancelled reviews do not mutate.
 */
export function createRouteSaveSession() {
    let busy = false;
    let draftBase = '';
    let reviewedDraft: FlightLeg[];
    return async (args: {
        flight: any;
        initialVisits: RouteVisit[];
        visits: RouteVisit[];
        tenant: {tpID: string; customerID: string; orgID: string};
        resolveLocations: (ids: string[]) => Promise<any[]>;
        review: (
            legs: FlightLeg[],
            save: (reviewed: FlightLeg[]) => Promise<void>
        ) => Promise<FlightLeg[] | undefined>;
        mutate: (variables: ReturnType<typeof routeMutationVariables>) => Promise<any>;
        isCurrent: () => boolean;
        /** Must explicitly approve changing the flight departure before assignment review opens. */
        confirmDepartureChange?: (departureID: string) => Promise<boolean>;
    }): Promise<boolean> => {
        if (busy) return false;
        busy = true;
        try {
            if (!args.isCurrent())
                throw new Error('The flight changed. Reload the current route before saving.');
            const departureID = args.visits[0]?.locationID;
            if (!departureID)
                throw new Error('Add a departure location before reviewing the route.');
            const departureChanged = departureID !== args.flight.departureID?._id;
            if (departureChanged) {
                if (!args.confirmDepartureChange)
                    throw new Error(
                        'Confirm the flight departure change before reviewing the route.'
                    );
                if (!(await args.confirmDepartureChange(departureID))) return false;
                if (!args.isCurrent())
                    throw new Error('The flight changed. Reload the current route before saving.');
            }

            const base = JSON.stringify([routeFingerprint(args.flight), args.initialVisits]);
            if (base !== draftBase) {
                draftBase = base;
                reviewedDraft = undefined;
            }
            const proposed = await prepareRouteEdit(
                args.flight,
                args.initialVisits,
                args.visits,
                args.resolveLocations,
                reviewedDraft,
                {allowDepartureChange: departureChanged}
            );
            let saved = false;

            /** Validates and persists while a reviewing dialog can still handle failure and retry. */
            const persistReviewed = async (reviewed: FlightLeg[]) => {
                const plan = assignmentPlanForLegs(reviewed);
                if (plan && needsAssignmentReview(plan))
                    throw new Error('Review affected assignments before saving.');
                if (!args.isCurrent())
                    throw new Error('The flight changed. Reload the current route before saving.');

                reviewedDraft = reviewed;
                await args.mutate(
                    routeMutationVariables(
                        args.flight._id,
                        reviewed,
                        args.tenant,
                        departureChanged ? departureID : undefined
                    )
                );
                reviewedDraft = undefined;
                saved = true;
            };

            const reviewed = await args.review(proposed, persistReviewed);
            if (saved) return true;
            if (!reviewed) return false;

            // Legacy callers may return a proposal instead of saving inside the review dialog.
            await persistReviewed(reviewed);
            return true;
        } finally {
            busy = false;
        }
    };
}

/** GraphQL flight fields consumed when changing departure from the details form. */
interface DepartureChangeFlight {
    departureID?: {_id: string};
    legs: string | FlightLeg[] | Record<string, FlightLeg>;
    paxIDList?: {_id: string}[];
    cgoIDList?: {_id: string}[];
}

type RemappedJourneyEndpoints = {departureID: string; destinationID: string};

/**
 * Prepares a details-form departure change and reviews its affected assignments.
 *
 * @param review - Returns accepted legs, or undefined when the user cancels.
 * @param confirmChange - Optional UI confirmation before endpoint remapping and assignment review.
 * @returns A departure/route patch and remapped entities for the optimistic response;
 * undefined on cancellation. Unchanged departures return an empty patch and the
 * original route and entity collections so ordinary details edits cannot overwrite them.
 *
 * @remarks
 * The leg manager applies the existing origin-remapping rules. This function does
 * not persist anything or modify the supplied flight; the caller performs the save.
 */
export async function prepareDepartureChange(
    flight: DepartureChangeFlight,
    departure: {_id: string; name: string},
    review: (legs: FlightLeg[]) => Promise<FlightLeg[] | undefined>,
    confirmChange?: () => Promise<boolean>
) {
    if (!departure?._id || departure._id === flight.departureID?._id) {
        return {
            patch: {},
            legs: flight.legs,
            pax: flight.paxIDList,
            cargo: flight.cgoIDList,
        };
    }

    const currentRoute = getFlightLegManagerFromFlightQuery(flight);
    const firstLeg = currentRoute.initialLegs[0];
    if (firstLeg && firstLeg.departureID !== flight.departureID?._id) {
        throw new Error(
            'Repair the route to start at the saved flight departure before changing departure.'
        );
    }

    if (confirmChange && !(await confirmChange())) {
        return;
    }

    const remappedRoute = getFlightLegManagerFromFlightQuery(flight, undefined, {
        id: departure._id,
        name: departure.name,
    });
    const proposedLegs = remappedRoute.buildFlightLegs({disableCreateMissingLegs: true});
    const reviewedLegs = await review(proposedLegs);
    if (!reviewedLegs) {
        return;
    }

    const assignmentPlan = assignmentPlanForLegs(reviewedLegs);
    if (assignmentPlan && needsAssignmentReview(assignmentPlan)) {
        throw new Error('Review affected assignments before changing departure.');
    }

    const passengerIDs = Array.from(new Set(reviewedLegs.flatMap((leg) => leg.paxIDs)));
    const cargoIDs = Array.from(new Set(reviewedLegs.flatMap((leg) => leg.cgoIDs)));
    const serializedLegs = JSON.stringify(Object.assign({}, reviewedLegs));

    /** Restores the GraphQL location shape needed by optimistic passenger/cargo rows. */
    function locationDetails(id: string) {
        return {__typename: 'Location', _id: id, name: remappedRoute.getLocationName(id)};
    }

    /**
     * Preserves the original entity fields while replacing its remapped journey endpoints.
     * Entities explicitly unassigned during review are excluded from the optimistic response.
     */
    function remapReviewedEntities<TEntity extends {_id: string}>(
        entities: TEntity[] | undefined,
        remappedByID: ReadonlyMap<string, RemappedJourneyEndpoints>,
        includedIDs: readonly string[]
    ) {
        return (entities || [])
            .filter((entity) => includedIDs.includes(entity._id))
            .map((entity) => {
                const endpoints = remappedByID.get(entity._id);
                return {
                    ...entity,
                    departureID: locationDetails(endpoints.departureID),
                    destinationID: locationDetails(endpoints.destinationID),
                };
            });
    }

    return {
        patch: {
            departureID: departure._id,
            legs: serializedLegs,
            paxIDList: passengerIDs,
            cgoIDList: cargoIDs,
        },
        legs: serializedLegs,
        pax: remapReviewedEntities(flight.paxIDList, remappedRoute.allPax, passengerIDs),
        cargo: remapReviewedEntities(flight.cgoIDList, remappedRoute.allCgo, cargoIDs),
    };
}
